import { useMemo, forwardRef, useState, useRef } from 'react';
import { Editor } from '@tiptap/core';

import * as Styled from './richtext-toolbar.style';
import RichtextToolbarDropdown from './richtext-toolbar-dropdown';
import { useMediaQuery, MEDIA_SM, MEDIA_MD } from 'approot/shared/style';
import RichtextToolbarDropdownItem from './richtext-toolbar-dropdown-item';
import {
  fontSize16,
  fontSize16LineHeight,
  FONT_CALIBRE_SCHOOL,
  FONT_NSW_HANDWRITING,
} from 'approot/shared/styles/typography.style';
import {
  isSelectionBold,
  isSelectionBulletList,
  isSelectionItalic,
  isSelectionOrderedList,
  isSelectionUnderlined,
  toggleBold,
  toggleBulletList,
  toggleItalic,
  toggleOrderedList,
  toggleUnderline,
  getFontFamily,
  setFontFamily,
  getFontFamilyLabel,
  setTextAlign,
  isTextAlign,
  setBlockHighlight,
} from './richtext.utils';
import {
  RICHTEXT_COLOUR_PALETTE,
  TEXT_ALIGN_CENTRE,
  TEXT_ALIGN_LEFT,
  TEXT_ALIGN_RIGHT,
} from './richtext-toolbar.constants';
import mergeRefs from 'lib/react/merge-refs';
import { useToolbarTrap } from './richtext.hooks';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { isMacPlatform } from 'approot/shared/ally/is-mac-platform';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import RichtextLinkModals from 'approot/shared/richtext/richtext-link-modal';
import { grey10 } from 'approot/shared/styles/colours.style';
import { spacing8 } from 'approot/shared/styles/spacing.style';
import { ColourTokenButton } from 'approot/shared/colour/colour-token';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from 'approot/activity-builder/activity-builder.constants';
import IconArrowLeft from '../icons/icon-arrow-left';

export type Props = {
  editor: Editor;
} & RichtextOptions;

export type RichtextOptions = {
  canChangeFont?: boolean;
  canChangeBlockType?: boolean;
};

enum VisibleToolbar {
  Left = 1,
  Right = 2,
}

const RichtextToolbar = forwardRef<HTMLDivElement | null, Props>(
  ({ editor, canChangeBlockType = true, canChangeFont = true }: Props, ref) => {
    const { isVisible, closeModal } = useSimpleModalState();
    const [visibleToolbar, setVisibleToolbar] = useState<VisibleToolbar>(
      VisibleToolbar.Left
    );
    const isMediumWidthUp = useMediaQuery(MEDIA_MD);
    const toolbarRef = useRef<HTMLDivElement | null>(null);

    const hideDropdowns = !canChangeBlockType && !canChangeFont;

    useToolbarTrap(toolbarRef, () => editor.chain().focus());

    return (
      <Styled.RichtextToolbar
        ref={mergeRefs<HTMLDivElement | null>([ref, toolbarRef])}
        className={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      >
        {isMediumWidthUp ? (
          <>
            {!hideDropdowns && (
              <RichtextToolbarDropdowns
                editor={editor}
                canChangeBlockType={canChangeBlockType}
                canChangeFont={canChangeFont}
              />
            )}

            <RichtextToolbarTextFormats editor={editor} />

            <Styled.RichtextDivider />

            <RichtextToolbarLists editor={editor} />

            <Styled.RichtextDivider />

            <RichtextToolbarTextAlign editor={editor} />

            <Styled.RichtextDivider />

            <Styled.RichtextDivider />

            <RichtextToolbarHighlight editor={editor} />

            <Styled.RichtextDivider />
          </>
        ) : (
          <>
            <>
              <Styled.RichtextSmallWrapper
                $hide={visibleToolbar === VisibleToolbar.Right}
              >
                {!hideDropdowns && (
                  <RichtextToolbarDropdowns
                    editor={editor}
                    canChangeBlockType={canChangeBlockType}
                    canChangeFont={canChangeFont}
                  />
                )}

                <RichtextToolbarTextFormats editor={editor} />

                <Styled.RichtextDivider />

                <Styled.RichtextArrowButton
                  $pointRight
                  onClick={() => setVisibleToolbar(VisibleToolbar.Right)}
                >
                  <IconArrowLeft />
                </Styled.RichtextArrowButton>
              </Styled.RichtextSmallWrapper>

              <Styled.RichtextSmallWrapper
                $hide={visibleToolbar === VisibleToolbar.Left}
              >
                <Styled.RichtextArrowButton
                  onClick={() => setVisibleToolbar(VisibleToolbar.Left)}
                >
                  <IconArrowLeft />
                </Styled.RichtextArrowButton>

                <Styled.RichtextDivider />

                <RichtextToolbarLists editor={editor} />

                <Styled.RichtextDivider />

                <RichtextToolbarTextAlign isDropdown editor={editor} />

                <Styled.RichtextDivider />

                <Styled.RichtextDivider />

                <RichtextToolbarHighlight editor={editor} />

                <Styled.RichtextDivider />
              </Styled.RichtextSmallWrapper>
            </>
          </>
        )}

        {isVisible && (
          <RichtextLinkModals editor={editor} closeModal={closeModal} />
        )}
      </Styled.RichtextToolbar>
    );
  }
);

export default RichtextToolbar;

const RichtextToolbarDropdowns = ({ editor, canChangeFont }: Props) => {
  const isSmallWidthUp = useMediaQuery(MEDIA_SM);

  // const currentBlockStyle = getBlockStyle(editor) || BLOCK_STYLE_PARAGRAPH;
  const currentFontFamily = getFontFamily(editor) || FONT_CALIBRE_SCHOOL;

  return (
    <>
      {
        // hidden until font sizes are confirmed
        /* {canChangeBlockType && (
        <>
          <RichtextToolbarDropdown
            ariaLabel="Choose a text size for the currently selected text"
            editor={editor}
            buttonElement={getBlockStyleLabel(currentBlockStyle)}
            tooltip="Styles"
            dropdownContents={
              <>
                <RichtextToolbarDropdownItem
                  isSelected={currentBlockStyle === BLOCK_STYLE_PARAGRAPH}
                  text="Medium"
                  onChange={() =>
                    setBlockStyle(
                      editor,
                      BLOCK_STYLE_PARAGRAPH,
                      currentFontFamily
                    )
                  }
                />
                <RichtextToolbarDropdownItem
                  isSelected={currentBlockStyle === BLOCK_STYLE_PARAGRAPH_SMALL}
                  text="Small"
                  onChange={() =>
                    setBlockStyle(
                      editor,
                      BLOCK_STYLE_PARAGRAPH_SMALL,
                      currentFontFamily
                    )
                  }
                />
              </>
            }
          />

          <Styled.RichtextDivider />
        </>
      )} */
      }

      {canChangeFont && (
        <>
          <RichtextToolbarDropdown
            ariaLabel="Choose a font for the currently selected text"
            editor={editor}
            buttonElement={
              isSmallWidthUp ? (
                getFontFamilyLabel(currentFontFamily)
              ) : (
                <ActivityBuilderInlineSvg id="icon_font" />
              )
            }
            tooltip="Font"
            dropdownContents={
              <>
                <RichtextToolbarDropdownItem
                  fontSize={fontSize16}
                  lineHeight={fontSize16LineHeight}
                  colour={grey10}
                  isSelected={currentFontFamily === FONT_CALIBRE_SCHOOL}
                  text="Calibre-R School"
                  onChange={() => setFontFamily(editor, FONT_CALIBRE_SCHOOL)}
                />
                <RichtextToolbarDropdownItem
                  fontSize={fontSize16}
                  lineHeight={fontSize16LineHeight}
                  colour={grey10}
                  isSelected={currentFontFamily === FONT_NSW_HANDWRITING}
                  text="NSW Handwriting"
                  onChange={() => setFontFamily(editor, FONT_NSW_HANDWRITING)}
                />
              </>
            }
          />

          <Styled.RichtextDivider />
        </>
      )}
    </>
  );
};

const RichtextToolbarLists = ({ editor }: Props) => {
  const isBulletList = isSelectionBulletList(editor);
  const isOrderedList = isSelectionOrderedList(editor);

  const metaKey = useMemo(() => (isMacPlatform() ? '&#8984;' : 'ctrl'), []);

  return (
    <Styled.RichtextIconGroup>
      <TooltipWrapper hoverText={`Bullets (${metaKey}+shift+8)`}>
        <Styled.RichtextIconButton
          $selected={isBulletList}
          onClick={() => toggleBulletList(editor)}
          aria-label={`${
            isBulletList
              ? 'Remove bullet list from the selected text'
              : 'Make the selected text a bullet list'
          }`}
        >
          <ActivityBuilderInlineSvg id="icon_bullet_list" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>

      <TooltipWrapper hoverText={`Numbers (${metaKey}+shift+7)`}>
        <Styled.RichtextIconButtonXsHidden
          $selected={isOrderedList}
          onClick={() => toggleOrderedList(editor)}
          aria-label={`${
            isOrderedList
              ? 'Remove numbered list from the selected text'
              : 'Make the selected text a numbered list'
          }`}
        >
          <ActivityBuilderInlineSvg id="icon_number_list" />
        </Styled.RichtextIconButtonXsHidden>
      </TooltipWrapper>
    </Styled.RichtextIconGroup>
  );
};

const RichtextToolbarTextFormats = ({ editor }: Props) => {
  const currentFontFamily = getFontFamily(editor) || FONT_CALIBRE_SCHOOL;
  const isBold = isSelectionBold(editor);
  const isItalic = isSelectionItalic(editor);
  const isUnderlined = isSelectionUnderlined(editor);

  const metaKey = useMemo(() => (isMacPlatform() ? '&#8984;' : 'ctrl'), []);

  return (
    <Styled.RichtextIconGroup>
      <TooltipWrapper hoverText={`Bold (${metaKey}+b)`}>
        <Styled.RichtextIconButton
          $selected={isBold}
          onClick={() => toggleBold(editor)}
          aria-label={`${
            isBold
              ? 'Remove bold from the selected text'
              : 'Make the selected text bold'
          }`}
        >
          <ActivityBuilderInlineSvg id="icon_bold" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>

      <TooltipWrapper hoverText={`Italic (${metaKey}+i)`}>
        <Styled.RichtextIconButton
          $selected={isItalic}
          onClick={() => toggleItalic(editor)}
          disabled={currentFontFamily === FONT_NSW_HANDWRITING}
          aria-label={`${
            isItalic
              ? 'Remove italic from the selected text'
              : 'Make the selected text italic'
          }`}
        >
          <ActivityBuilderInlineSvg id="icon_italic" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>

      <TooltipWrapper hoverText={`Underline (${metaKey}+u)`}>
        <Styled.RichtextIconButton
          $selected={isUnderlined}
          onClick={() => toggleUnderline(editor)}
          aria-label={`${
            isUnderlined
              ? 'Remove underline from the selected text'
              : 'Make the selected text underlined'
          }`}
        >
          <ActivityBuilderInlineSvg id="icon_underline" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>
    </Styled.RichtextIconGroup>
  );
};

const RichtextToolbarTextAlign = ({
  editor,
  isDropdown,
}: Props & { isDropdown?: boolean }) => {
  const isTextAlignLeft = isTextAlign(editor, TEXT_ALIGN_LEFT);
  const isTextAlignCentre = isTextAlign(editor, TEXT_ALIGN_CENTRE);
  const isTextAlignRight = isTextAlign(editor, TEXT_ALIGN_RIGHT);

  const metaKey = useMemo(() => (isMacPlatform() ? '&#8984;' : 'ctrl'), []);

  return isDropdown ? (
    <RichtextToolbarDropdown
      isRow
      ariaLabel="Choose a text alignment for the currently selected text"
      editor={editor}
      buttonElement={<ActivityBuilderInlineSvg id="icon_align_left" />}
      tooltip="Text align"
      dropdownContents={
        <>
          <Styled.RichtextIconButton
            onClick={() => setTextAlign(editor, TEXT_ALIGN_LEFT)}
            data-dropdown-item={true}
            $selected={isTextAlignLeft}
          >
            <ActivityBuilderInlineSvg id="icon_align_left" />
          </Styled.RichtextIconButton>
          <Styled.RichtextIconButton
            onClick={() => setTextAlign(editor, TEXT_ALIGN_CENTRE)}
            data-dropdown-item={true}
            $selected={isTextAlignCentre}
          >
            <ActivityBuilderInlineSvg id="icon_align_centre" />
          </Styled.RichtextIconButton>
          <Styled.RichtextIconButton
            onClick={() => setTextAlign(editor, TEXT_ALIGN_RIGHT)}
            data-dropdown-item={true}
            $selected={isTextAlignRight}
          >
            <ActivityBuilderInlineSvg id="icon_align_right" />
          </Styled.RichtextIconButton>
        </>
      }
    />
  ) : (
    <Styled.RichtextIconGroup>
      <TooltipWrapper hoverText={`Align left (${metaKey}+shift+L)`}>
        <Styled.RichtextIconButton
          $selected={isTextAlignLeft}
          onClick={() => setTextAlign(editor, TEXT_ALIGN_LEFT)}
          aria-label="Make the selected text left aligned"
        >
          <ActivityBuilderInlineSvg id="icon_align_left" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>

      <TooltipWrapper hoverText={`Align centre (${metaKey}+shift+E)`}>
        <Styled.RichtextIconButton
          $selected={isTextAlignCentre}
          onClick={() => setTextAlign(editor, TEXT_ALIGN_CENTRE)}
          aria-label="Make the selected text centre aligned"
        >
          <ActivityBuilderInlineSvg id="icon_align_centre" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>

      <TooltipWrapper hoverText={`Align right (${metaKey}+shift+R)`}>
        <Styled.RichtextIconButton
          $selected={isTextAlignRight}
          onClick={() => setTextAlign(editor, TEXT_ALIGN_RIGHT)}
          aria-label="Make the selected text right aligned"
        >
          <ActivityBuilderInlineSvg id="icon_align_right" />
        </Styled.RichtextIconButton>
      </TooltipWrapper>
    </Styled.RichtextIconGroup>
  );
};

const RichtextToolbarHighlight = ({ editor }: Props) => {
  const highlightedColour = RICHTEXT_COLOUR_PALETTE.filter(c =>
    editor.isActive('highlight', {
      color: c.colour2,
    })
  );

  return (
    <RichtextToolbarDropdown
      ariaLabel="Choose a highlight colour for the currently selected text"
      editor={editor}
      buttonElement={<ActivityBuilderInlineSvg id="icon_highlight" />}
      tooltip="Highlight"
      dropdownContents={
        <>
          <Styled.RichtextColourPanelHeading marginBottom={spacing8}>
            Choose colour
          </Styled.RichtextColourPanelHeading>
          <Styled.RichtextColourPanel
            $marginBottom={highlightedColour.length > 0 ? 16 : 0}
          >
            {RICHTEXT_COLOUR_PALETTE.map(colour => (
              <ColourTokenButton
                key={colour.base}
                colours={colour}
                isActive={editor.isActive('highlight', {
                  color: colour.colour2,
                })}
                onClick={() => {
                  setBlockHighlight(editor, colour.colour2);
                }}
              />
            ))}
          </Styled.RichtextColourPanel>
          {highlightedColour.length > 0 && (
            <SecondaryButton
              type="button"
              onClick={() =>
                setBlockHighlight(editor, highlightedColour[0].colour2)
              }
            >
              Remove Highlight
            </SecondaryButton>
          )}
        </>
      }
    />
  );
};
